import React, { memo } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { Typography } from "@kelp-dao/kernel-design-system";
import { TChartData, TMarkerPoint } from "@components/Charts/configs";
import BaseImage from "@components/BaseImage";
import VaultAreaChart from "./charts/VaultAreaChart";
import { TimeframeOption } from "@constants/types";
import { trackEvent } from "utils";
import { VAULT_PERFORMANCE_TIMEFRAME_CHANGE } from "@constants/analytics";

interface ChartAreaProps {
  chartData?: TChartData & {
    selectedTimeframeApr?: number | null;
    aprLabel?: string;
    validTimeframes?: TimeframeOption[];
  };
  markerPoints: TMarkerPoint[];
  showTimeframeSelector?: boolean;
  onTimeframeChange?: (timeframe: TimeframeOption) => void;
  tokenType: "agETH" | "hgETH" | "sbUSD";
  aprTooltip?: string;
  activeTimeframe?: TimeframeOption;
}

// Timeframe selector component
const TimeframeSelector = memo(
  ({
    activeTimeframe,
    onChange,
    validTimeframes,
  }: {
    activeTimeframe: TimeframeOption;
    onChange: (timeframe: TimeframeOption) => void;
    validTimeframes?: TimeframeOption[];
  }) => {
    // Use validTimeframes if provided, otherwise fallback to default based on tokenType
    const timeframes: TimeframeOption[] = validTimeframes || [];

    return (
      <div className="inline-flex items-center gap-2 rounded-lg bg-bg-primary border border-border-default border-1">
        {timeframes.map((timeframe) => (
          <button
            key={timeframe}
            onClick={() => onChange(timeframe)}
            className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${
              activeTimeframe === timeframe
                ? "text-text-primary bg-white font-regular"
                : "text-text-tertiary hover:text-text-primary"
            }`}
          >
            {timeframe}
          </button>
        ))}
      </div>
    );
  }
);

TimeframeSelector.displayName = "TimeframeSelector";

// APR Display component
const AprDisplay = memo(
  ({
    apr,
    label,
  }: {
    apr: number | null | undefined;
    label: string;
    tooltipText?: string;
  }) => {
    if (apr === null || apr === undefined) return null;

    return (
      <div className="flex flex-col gap-1">
        <Typography
          type="heading"
          variant="large"
          className="text-text-primary font-medium text-[20px] md:text-[24px] lg:text-[28px] leading-none font-sans"
        >
          {apr.toFixed(2)}
          <span className="text-[20px] font-normal"> %</span>
        </Typography>
        <Typography
          type="body"
          variant="xsmall"
          className="text-text-secondary font-normal text-xs leading-[1.2]"
        >
          {label}
        </Typography>
      </div>
    );
  }
);

AprDisplay.displayName = "AprDisplay";

// Placeholder component for when no chart data is available
const ChartPlaceholder = memo(() => (
  <div className="relative rounded-xl">
    <div className="relative blur-md pointer-events-none opacity-60 h-[330px] sm:h-[380px] tablet:h-[340px] overflow-hidden">
      <BaseImage
        src="/assets/vaults/blurred_chart.svg"
        alt="placeholder chart"
        width={100}
        height={100}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    </div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1] flex items-center justify-center text-center w-full">
      <Typography
        type="body"
        variant="small"
        className="text-sm tablet:text-base w-full"
      >
        Performance charts coming soon
      </Typography>
    </div>
  </div>
));

ChartPlaceholder.displayName = "ChartPlaceholder";

// Main chart area component - consolidated from ChartArea and PerformanceChart
const ChartArea = memo(function ChartArea({
  chartData,
  markerPoints,
  showTimeframeSelector = true,
  onTimeframeChange,
  tokenType,
  aprTooltip,
  activeTimeframe = "1M",
}: ChartAreaProps) {
  const handleTimeframeChange = (timeframe: TimeframeOption) => {
    onTimeframeChange?.(timeframe);
    trackEvent(VAULT_PERFORMANCE_TIMEFRAME_CHANGE, {
      vault_type: tokenType,
      timeframe: timeframe,
    });
  };

  // Show placeholder if no chart data
  if (!chartData) {
    return <ChartPlaceholder />;
  }

  const { selectedTimeframeApr, aprLabel, validTimeframes } = chartData;

  return (
    <div className="w-full">
      <Flex direction={"column"}>
        {/* Header with APR and Timeframe Selector */}
        {showTimeframeSelector && (
          <Flex
            direction={{ base: "column", sm: "row" }}
            justify={{ base: "flex-start", sm: "space-between" }}
            align={{ base: "flex-start", sm: "center" }}
            mb={3}
            gap={{ base: 3, md: 0 }}
            px={2}
            pt={3}
          >
            {/* APR Display */}
            <AprDisplay
              apr={selectedTimeframeApr}
              label={aprLabel ?? ""}
              tooltipText={aprTooltip}
            />
            <Flex flex={1} />
            {/* Timeframe Selector */}
            <TimeframeSelector
              activeTimeframe={activeTimeframe}
              onChange={handleTimeframeChange}
              validTimeframes={validTimeframes}
            />
          </Flex>
        )}
        <Box
          className="relative"
          height={{ base: 330, sm: 380, tablet: 340 }}
          minHeight={{ base: 330, sm: 380, tablet: 340 }}
        >
          <VaultAreaChart
            chartData={chartData}
            markerPoints={markerPoints}
            tokenType={tokenType}
          />
        </Box>
      </Flex>
    </div>
  );
});

export default ChartArea;
